import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

function DiseasesIndex() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [icdStatus, setIcdStatus] = useState('checking');

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const numbers = ['#'];

  const handleLetterClick = (letter) => {
    navigate(`/diseases/${letter}`);
    window.scrollTo(0, 0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/diseases/${searchQuery.charAt(0).toUpperCase()}?search=${encodeURIComponent(searchQuery)}`);
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchIcdStatus = async () => {
      try {
        const response = await axios.get('/api/diseases/icd/status');
        if (!isMounted) return;
        setIcdStatus(response.data?.configured ? 'connected' : 'not_configured');
      } catch (_error) {
        if (!isMounted) return;
        setIcdStatus('unavailable');
      }
    };

    fetchIcdStatus();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      {/* Hero Section - White Background */}
      <section className="py-5" style={{ backgroundColor: '#fff', color: '#333', minHeight: '400px' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: 'var(--tgh-blue-light)' }}>Home</Link></li>
              <li className="breadcrumb-item active" style={{ color: '#666' }}>Diseases & Conditions</li>
            </ol>
          </nav>

          <h1 className="display-4 mb-3" style={{ fontFamily: 'Lora, serif', fontWeight: 'bold', color: 'var(--tgh-blue)' }}>
            Diseases & Conditions
          </h1>
          <div className="mb-3">
            <span
              className={`badge rounded-pill ${
                icdStatus === 'connected'
                  ? 'bg-success-subtle text-success-emphasis'
                  : icdStatus === 'checking'
                    ? 'bg-secondary-subtle text-secondary-emphasis'
                    : 'bg-warning-subtle text-warning-emphasis'
              }`}
              style={{ fontSize: '0.78rem', padding: '0.45rem 0.75rem' }}
            >
              {icdStatus === 'connected'
                ? 'ICD-11 connected'
                : icdStatus === 'checking'
                  ? 'Checking ICD-11 connection...'
                  : 'ICD-11 not configured'}
            </span>
          </div>
          <p className="lead mb-5" style={{ fontSize: '1.1rem', color: '#666' }}>
            Easy-to-understand answers about diseases and conditions
          </p>
          <p className="tgh-diseases-disclaimer mb-4" role="note">
            This is for informational purposes only. For medical advice or a diagnosis, consult a professional or
            healthcare provider.
          </p>

          <div className="row mt-4">
            <div className="col-lg-6">
              <label className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#333' }}>
                Search diseases & conditions
              </label>
              <form onSubmit={handleSearch}>
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ borderRadius: '8px 0 0 8px', border: '1px solid #ddd' }}
                  />
                  <button className="btn btn-primary" type="submit" style={{ borderRadius: '0 8px 8px 0', backgroundColor: 'var(--tgh-blue)', borderColor: 'var(--tgh-blue)' }}>
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
            <div className="col-lg-6">
              <label className="mb-2" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#333' }}>
                Find diseases & conditions by first letter
              </label>
              <div className="d-flex flex-wrap gap-2">
                {alphabet.map((letter) => (
                  <button
                    key={letter}
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => handleLetterClick(letter)}
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      fontWeight: '600',
                      border: '2px solid var(--tgh-blue)',
                      color: 'var(--tgh-blue)',
                      fontSize: '1rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--tgh-blue)';
                      e.target.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = 'var(--tgh-blue)';
                    }}
                  >
                    {letter}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => handleLetterClick('#')}
                  style={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '50%',
                    fontWeight: '600',
                    border: '2px solid var(--tgh-blue)',
                    color: 'var(--tgh-blue)',
                    fontSize: '1rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--tgh-blue)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = 'var(--tgh-blue)';
                  }}
                >
                  #
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Cards Section - White Background */}
      <section className="py-5" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="row g-4 mb-5">
            {/* Symptoms browse card */}
            <div className="col-md-12 mb-4">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                <div className="row g-0" style={{ minHeight: '300px' }}>
                  <div className="col-md-6">
                    <div className="card-body p-4" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', minHeight: '300px' }}>
                      <div className="mb-2" style={{ fontSize: '1.75rem' }}>
                        <i className="bi bi-heart-pulse" style={{ color: 'var(--tgh-blue)' }}></i>
                      </div>
                      <h3 className="card-title mb-2" style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>Symptoms</h3>
                      <p className="card-text text-muted mb-2" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                        Browse symptoms from our articles and see related diseases and conditions.
                      </p>
                      <Link to="/symptoms" className="text-decoration-none" style={{ color: 'var(--tgh-blue-light)', fontWeight: '600', fontSize: '0.95rem' }}>
                        Browse symptoms <i className="bi bi-arrow-right ms-1"></i>
                      </Link>
                    </div>
                  </div>
                  <div className="col-md-6" style={{ display: 'flex', alignItems: 'stretch', height: '300px', overflow: 'hidden' }}>
                    <img 
                      src="/img/carousel/admin2.jpg" 
                      alt="Symptoms" 
                      className="img-fluid"
                      style={{ 
                        width: '100%', 
                        height: '300px',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/600x400/003d82/ffffff?text=Symptoms';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Section - Dark Background Banner */}
          <div className="row align-items-center mb-5" style={{ backgroundColor: 'var(--tgh-blue)', borderRadius: '12px', padding: '3rem 2rem', margin: '0' }}>
            <div className="col-lg-6">
              <h2 className="mb-3" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fff' }}>
                The right answers, the first time
              </h2>
              <p className="lead mb-3" style={{ fontSize: '1.1rem', color: '#fff' }}>
                Tema General Hospital experts solve the world's toughest medical problems — one patient at a time.
              </p>
              <Link to="/about-us/overview" className="text-decoration-none" style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>
                Learn about TGH <i className="bi bi-arrow-right ms-1"></i>
              </Link>
            </div>
            <div className="col-lg-6">
              <img 
                src="/img/carousel/admin2.jpg" 
                alt="TGH Healthcare Professional" 
                className="img-fluid rounded"
                style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/600x400/003d82/ffffff?text=TGH+Healthcare';
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default DiseasesIndex;
